import React from "react";

import Tag from "@/components/common/Tag/Tag";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { cn } from "@/utils/general";

export type ScienceSectionIntroProps = {
  tag?: string;
  headline?: string;
  text?: string;
  /** Light (default) or dark section surfaces. */
  background?: "light" | "dark";
  /**
   * Title column width from `lg` (1024) up, as a fraction of the row.
   * Matches Gatsby SectionIntro `headingWidth` (default 0.46).
   */
  headingWidth?: number;
  /**
   * Bottom padding from `lg` up in px. Matches Gatsby `lgpb` (default 80).
   */
  lgPaddingBottom?: 40 | 80;
  className?: string;
};

export default function ScienceSectionIntro({
  tag,
  headline,
  text,
  background = "light",
  headingWidth = 0.46,
  lgPaddingBottom = 80,
  className,
}: ScienceSectionIntroProps) {
  const isDark = background === "dark";
  const color = isDark ? "text-nova-base-white" : "text-nova-base-black";
  const colorBorder = isDark
    ? "border-t-nova-transparency-white-20"
    : "border-t-nova-transparency-black-20";

  return (
    <div
      className={cn(
        "px-6 md:px-0 flex flex-col items-start pb-10",
        lgPaddingBottom === 40 ? "lg:pb-10" : "lg:pb-20",
        className
      )}
    >
      {tag && <Tag data={{ line: tag, classes: "bg-nova-brand-accent-80" }} />}
      <div className={cn("flex flex-wrap w-full justify-between mt-4 pt-10 border-t", colorBorder)}>
        <div
          className="w-full lg:w-[var(--science-intro-heading-width)]"
          style={
            {
              "--science-intro-heading-width": `${headingWidth * 100}%`,
            } as React.CSSProperties
          }
        >
          {headline && (
            <TextElementNova
              element="h2"
              className={cn("Title-20-Super-Normal", color)}
              text={headline}
            />
          )}
        </div>
        <div className="w-full lg:w-[38%] mt-4 md:mt-5">
          {text && (
            <TextElementNova element="p" className={cn("Body-30-Dia-Normal", color)} text={text} />
          )}
        </div>
      </div>
    </div>
  );
}
